import React, { useState, useEffect } from "react";
import {
  getDailyAppInstallsStatsForAdmin,
  getMonthlyAppInstallsStatsForAdmin,
  getLifetimeAppInstallsStatsForAdmin,
  getLifetimeEngagementTimeStatsForAdmin,
  getDailyEngagementTimeStatsForAdmin,
  getMonthlyEngagementTimeStatsForAdmin,
  AppInstallsStatsResponse,
  MonthlyAppInstallsStatsResponse,
  LifetimeAppInstallsStatsResponse,
  LifetimeEngagementTimeStatsResponse,
  EngagementTimeStatsResponse,
  MonthlyEngagementTimeStatsResponse,
} from "../api/stats";
import { Loader } from "../components/Loader";
import { Button } from "../components/Button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  isValid,
  parseISO,
  addMonths,
  getMonth,
  getYear,
} from "date-fns";

const BAR_COLORS = {
  ios: "rgb(99 102 241 / 0.7)", // indigo for iOS
  android: "rgb(34 197 94 / 0.7)", // green for Android
  web: "rgb(236 72 153 / 0.7)", // pink for Web
  total: "rgb(245 158 11 / 0.7)", // amber for total
};

const PLATFORM_COLORS = {
  iOS: "rgb(99 102 241 / 0.7)", // indigo
  Android: "rgb(34 197 94 / 0.7)", // green
  Web: "rgb(236 72 153 / 0.7)", // pink
};

type DailyDateRange = "7days" | "30days" | "2months" | "custom";
type MonthlyDateRange =
  | "last3Months"
  | "last6Months"
  | "last12Months"
  | "custom";

const AdminAppPerformance: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lifetimeStats, setLifetimeStats] =
    useState<LifetimeAppInstallsStatsResponse | null>(null);
  const [lifetimeStatsLoading, setLifetimeStatsLoading] = useState(true);
  const [lifetimeStatsError, setLifetimeStatsError] = useState<string | null>(
    null
  );
  const [lifetimeEngagementStats, setLifetimeEngagementStats] =
    useState<LifetimeEngagementTimeStatsResponse | null>(null);
  const [lifetimeEngagementStatsError, setLifetimeEngagementStatsError] =
    useState<string | null>(null);
  const [appInstallsStats, setAppInstallsStats] =
    useState<AppInstallsStatsResponse | null>(null);
  const [monthlyAppInstallsStats, setMonthlyAppInstallsStats] =
    useState<MonthlyAppInstallsStatsResponse | null>(null);
  const [engagementTimeStats, setEngagementTimeStats] =
    useState<EngagementTimeStatsResponse | null>(null);
  const [monthlyEngagementTimeStats, setMonthlyEngagementTimeStats] =
    useState<MonthlyEngagementTimeStatsResponse | null>(null);
  const [engagementTimeError, setEngagementTimeError] = useState<string | null>(
    null
  );
  const [engagementTimeLoading, setEngagementTimeLoading] = useState(false);

  // Filters
  const [groupBy, setGroupBy] = useState<"daily" | "monthly">("monthly");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [fromMonth, setFromMonth] = useState<string>("");
  const [toMonth, setToMonth] = useState<string>("");
  const [activeDailyRange, setActiveDailyRange] =
    useState<DailyDateRange>("7days");
  const [activeMonthlyRange, setActiveMonthlyRange] =
    useState<MonthlyDateRange>("last12Months");

  // Fetch lifetime stats on component mount
  useEffect(() => {
    fetchLifetimeAppInstallsStats();
    fetchLifetimeEngagementStats();
  }, []);

  // Generate a list of months from November 2024 until now
  const generateMonthOptions = () => {
    const options = [];
    const startDate = new Date(2024, 10, 1); // Nov 2024 (0-indexed months, so 10 is November)
    const currentDate = new Date();
    let date = startDate;

    while (date <= currentDate) {
      const monthValue = format(date, "yyyy-MM");
      const monthLabel = format(date, "MMM yyyy");
      options.push({
        value: monthValue,
        label: monthLabel,
      });
      date = addMonths(date, 1);
    }

    return options.reverse(); // Most recent first
  };

  const monthOptions = generateMonthOptions();

  // Initialize default range on component mount
  useEffect(() => {
    if (groupBy === "daily") {
      handleDailyRangeClick("7days");
    } else {
      handleMonthlyRangeClick("last12Months");
    }
  }, [groupBy]);

  // Fetch data when filters change
  useEffect(() => {
    if (groupBy === "daily" && (fromDate || toDate)) {
      fetchDailyAppInstallsStats();
      fetchDailyEngagementTimeStats();
    } else if (groupBy === "monthly" && (fromMonth || toMonth)) {
      fetchMonthlyAppInstallsStats();
      fetchMonthlyEngagementTimeStats();
    }
  }, [fromDate, toDate, fromMonth, toMonth, groupBy]);

  const fetchLifetimeAppInstallsStats = async () => {
    try {
      setLifetimeStatsLoading(true);
      const response = await getLifetimeAppInstallsStatsForAdmin();
      setLifetimeStats(response);
      setLifetimeStatsError(null);
    } catch (err) {
      setLifetimeStatsError(
        "Failed to fetch lifetime app downloads statistics"
      );
      console.error("Error fetching lifetime app downloads statistics:", err);
    } finally {
      setLifetimeStatsLoading(false);
    }
  };

  const fetchLifetimeEngagementStats = async () => {
    try {
      const response = await getLifetimeEngagementTimeStatsForAdmin();
      setLifetimeEngagementStats(response);
      setLifetimeEngagementStatsError(null);
    } catch (err) {
      setLifetimeEngagementStatsError(
        "Failed to fetch lifetime engagement statistics"
      );
      console.error("Error fetching lifetime engagement statistics:", err);
    }
  };

  const fetchDailyAppInstallsStats = async () => {
    try {
      setLoading(true);
      const response = await getDailyAppInstallsStatsForAdmin(
        fromDate || undefined,
        toDate || undefined
      );
      setAppInstallsStats(response);
      setMonthlyAppInstallsStats(null);
      setError(null);
    } catch (err) {
      setError("Failed to fetch daily app downloads statistics");
      console.error("Error fetching daily app downloads statistics:", err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchMonthlyAppInstallsStats = async () => {
    try {
      setLoading(true);
      const response = await getMonthlyAppInstallsStatsForAdmin(
        fromMonth || undefined,
        toMonth || undefined
      );
      setMonthlyAppInstallsStats(response);
      setAppInstallsStats(null);
      setError(null);
    } catch (err) {
      setError("Failed to fetch monthly app downloads statistics");
      console.error("Error fetching monthly app downloads statistics:", err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchDailyEngagementTimeStats = async () => {
    try {
      setEngagementTimeLoading(true);
      const response = await getDailyEngagementTimeStatsForAdmin(
        fromDate || undefined,
        toDate || undefined
      );
      setEngagementTimeStats(response);
      setMonthlyEngagementTimeStats(null);
      setEngagementTimeError(null);
    } catch (err) {
      setEngagementTimeError(
        "Failed to fetch daily engagement time statistics"
      );
      console.error("Error fetching daily engagement time statistics:", err);
    } finally {
      setEngagementTimeLoading(false);
    }
  };

  const fetchMonthlyEngagementTimeStats = async () => {
    try {
      setEngagementTimeLoading(true);
      const response = await getMonthlyEngagementTimeStatsForAdmin(
        fromMonth || undefined,
        toMonth || undefined
      );
      setMonthlyEngagementTimeStats(response);
      setEngagementTimeStats(null);
      setEngagementTimeError(null);
    } catch (err) {
      setEngagementTimeError(
        "Failed to fetch monthly engagement time statistics"
      );
      console.error("Error fetching monthly engagement time statistics:", err);
    } finally {
      setEngagementTimeLoading(false);
    }
  };

  const handleDailyRangeClick = (range: DailyDateRange) => {
    const today = new Date();
    let from = "";
    let to = format(today, "yyyy-MM-dd");

    switch (range) {
      case "7days":
        from = format(subDays(today, 7), "yyyy-MM-dd");
        break;
      case "30days":
        from = format(subDays(today, 30), "yyyy-MM-dd");
        break;
      case "2months":
        // First day of previous month
        from = format(startOfMonth(subMonths(today, 1)), "yyyy-MM-dd");
        break;
      case "custom":
        // Keep existing dates for custom
        return setActiveDailyRange("custom");
    }

    setFromDate(from);
    setToDate(to);
    setActiveDailyRange(range);
  };

  const handleMonthlyRangeClick = (range: MonthlyDateRange) => {
    const today = new Date();
    let from = "";
    let to = format(today, "yyyy-MM");

    switch (range) {
      case "last3Months":
        from = format(subMonths(today, 2), "yyyy-MM");
        break;
      case "last6Months":
        from = format(subMonths(today, 5), "yyyy-MM");
        break;
      case "last12Months":
        from = format(subMonths(today, 11), "yyyy-MM");
        break;
      case "custom":
        // Keep existing dates for custom
        return setActiveMonthlyRange("custom");
    }

    setFromMonth(from);
    setToMonth(to);
    setActiveMonthlyRange(range);
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setFromDate(newDate);
    setActiveDailyRange("custom");
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setToDate(newDate);
    setActiveDailyRange("custom");
  };

  const handleFromMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = e.target.value;
    setFromMonth(newMonth);
    setActiveMonthlyRange("custom");
  };

  const handleToMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = e.target.value;
    setToMonth(newMonth);
    setActiveMonthlyRange("custom");
  };

  const handleGroupByChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupBy(e.target.value as "daily" | "monthly");
  };

  const prepareDailyChartData = (stats: AppInstallsStatsResponse) => {
    // For daily grouping
    const combinedData = stats.ios.map((iosData) => {
      const androidData = stats.android.find(
        (data) => data.date === iosData.date
      );
      const webData = stats.web.find((data) => data.date === iosData.date);
      const androidCount = androidData ? androidData.count : 0;
      const webCount = webData ? webData.count : 0;
      return {
        date: format(new Date(iosData.date), "MMM dd"),
        ios: iosData.count,
        android: androidCount,
        web: webCount,
        total: iosData.count + androidCount + webCount,
      };
    });

    // Add any Android data that doesn't have matching iOS data
    stats.android.forEach((androidData) => {
      const exists = combinedData.some(
        (data) => data.date === format(new Date(androidData.date), "MMM dd")
      );
      if (!exists) {
        const webData = stats.web.find((data) => data.date === androidData.date);
        const webCount = webData ? webData.count : 0;
        combinedData.push({
          date: format(new Date(androidData.date), "MMM dd"),
          ios: 0,
          android: androidData.count,
          web: webCount,
          total: androidData.count + webCount,
        });
      }
    });

    // Add any Web data that doesn't have matching iOS or Android data
    stats.web.forEach((webData) => {
      const exists = combinedData.some(
        (data) => data.date === format(new Date(webData.date), "MMM dd")
      );
      if (!exists) {
        combinedData.push({
          date: format(new Date(webData.date), "MMM dd"),
          ios: 0,
          android: 0,
          web: webData.count,
          total: webData.count,
        });
      }
    });

    // Sort by date
    return combinedData.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  };

  const prepareMonthlyChartData = (stats: MonthlyAppInstallsStatsResponse) => {
    // For monthly grouping
    const combinedData = stats.ios.map((iosData) => {
      const androidData = stats.android.find(
        (data) => data.month === iosData.month
      );
      const webData = stats.web.find((data) => data.month === iosData.month);
      const androidCount = androidData ? androidData.count : 0;
      const webCount = webData ? webData.count : 0;
      return {
        date: format(new Date(iosData.month + "-01"), "MMM yyyy"),
        ios: iosData.count,
        android: androidCount,
        web: webCount,
        total: iosData.count + androidCount + webCount,
      };
    });

    // Add any Android data that doesn't have matching iOS data
    stats.android.forEach((androidData) => {
      const exists = combinedData.some(
        (data) =>
          data.date === format(new Date(androidData.month + "-01"), "MMM yyyy")
      );
      if (!exists) {
        const webData = stats.web.find((data) => data.month === androidData.month);
        const webCount = webData ? webData.count : 0;
        combinedData.push({
          date: format(new Date(androidData.month + "-01"), "MMM yyyy"),
          ios: 0,
          android: androidData.count,
          web: webCount,
          total: androidData.count + webCount,
        });
      }
    });

    // Add any Web data that doesn't have matching iOS or Android data
    stats.web.forEach((webData) => {
      const exists = combinedData.some(
        (data) =>
          data.date === format(new Date(webData.month + "-01"), "MMM yyyy")
      );
      if (!exists) {
        combinedData.push({
          date: format(new Date(webData.month + "-01"), "MMM yyyy"),
          ios: 0,
          android: 0,
          web: webData.count,
          total: webData.count,
        });
      }
    });

    // Sort by date
    return combinedData.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  };

  // Prepare data for lifetime stats pie chart
  const prepareLifetimeChartData = (
    stats: LifetimeAppInstallsStatsResponse
  ) => {
    const total = stats.totalIos + stats.totalAndroid + stats.totalWeb;
    const iosPercentage =
      total === 0 ? "0.0" : ((stats.totalIos / total) * 100).toFixed(1);
    const androidPercentage =
      total === 0 ? "0.0" : ((stats.totalAndroid / total) * 100).toFixed(1);
    const webPercentage =
      total === 0 ? "0.0" : ((stats.totalWeb / total) * 100).toFixed(1);

    return [
      {
        name: "iOS",
        value: stats.totalIos,
        label: `iOS ${iosPercentage}%`,
        color: PLATFORM_COLORS.iOS,
      },
      {
        name: "Android",
        value: stats.totalAndroid,
        label: `Android ${androidPercentage}%`,
        color: PLATFORM_COLORS.Android,
      },
      {
        name: "Web",
        value: stats.totalWeb,
        label: `Web ${webPercentage}%`,
        color: PLATFORM_COLORS.Web,
      },
    ];
  };

  // Custom legend renderer for pie chart
  const renderCustomLegend = (data: any[]) => (
    <ul className="flex justify-center mt-4">
      {data.map((entry, index) => (
        <li key={`item-${index}`} className="mx-2">
          <span
            style={{
              display: "inline-block",
              width: "10px",
              height: "10px",
              backgroundColor: data[index].color,
              marginRight: "5px",
            }}
          />
          {entry.label}
        </li>
      ))}
    </ul>
  );

  // Helper function to format seconds into human-readable time
  const formatEngagementTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours.toLocaleString()}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes.toLocaleString()}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const prepareDailyEngagementChartData = (
    stats: EngagementTimeStatsResponse
  ) => {
    // For daily grouping
    const combinedData = stats.ios.map((iosData) => {
      const androidData = stats.android.find(
        (data) => data.date === iosData.date
      );
      const webData = stats.web.find((data) => data.date === iosData.date);
      const androidSeconds = androidData ? androidData.secondsCount : 0;
      const webSeconds = webData ? webData.secondsCount : 0;
      return {
        date: format(new Date(iosData.date), "MMM dd"),
        ios: Math.round(iosData.secondsCount / 60), // Convert to minutes
        android: Math.round(androidSeconds / 60), // Convert to minutes
        web: Math.round(webSeconds / 60), // Convert to minutes
        average: Math.round((iosData.secondsCount + androidSeconds + webSeconds) / 3 / 60), // Average in minutes
      };
    });

    // Add any Android data that doesn't have matching iOS data
    stats.android.forEach((androidData) => {
      const exists = combinedData.some(
        (data) => data.date === format(new Date(androidData.date), "MMM dd")
      );
      if (!exists) {
        const webData = stats.web.find((data) => data.date === androidData.date);
        const webSeconds = webData ? webData.secondsCount : 0;
        combinedData.push({
          date: format(new Date(androidData.date), "MMM dd"),
          ios: 0,
          android: Math.round(androidData.secondsCount / 60), // Convert to minutes
          web: Math.round(webSeconds / 60), // Convert to minutes
          average: Math.round((androidData.secondsCount + webSeconds) / 2 / 60), // Average in minutes
        });
      }
    });

    // Add any Web data that doesn't have matching iOS or Android data
    stats.web.forEach((webData) => {
      const exists = combinedData.some(
        (data) => data.date === format(new Date(webData.date), "MMM dd")
      );
      if (!exists) {
        combinedData.push({
          date: format(new Date(webData.date), "MMM dd"),
          ios: 0,
          android: 0,
          web: Math.round(webData.secondsCount / 60), // Convert to minutes
          average: Math.round(webData.secondsCount / 60), // Average in minutes
        });
      }
    });

    // Sort by date
    return combinedData.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  };

  const prepareMonthlyEngagementChartData = (
    stats: MonthlyEngagementTimeStatsResponse
  ) => {
    // For monthly grouping
    const combinedData = stats.ios.map((iosData) => {
      const androidData = stats.android.find(
        (data) => data.month === iosData.month
      );
      const webData = stats.web.find((data) => data.month === iosData.month);
      const androidSeconds = androidData ? androidData.secondsCount : 0;
      const webSeconds = webData ? webData.secondsCount : 0;
      return {
        date: format(new Date(iosData.month + "-01"), "MMM yyyy"),
        ios: Math.round(iosData.secondsCount / 60), // Convert to minutes
        android: Math.round(androidSeconds / 60), // Convert to minutes
        web: Math.round(webSeconds / 60), // Convert to minutes
        average: Math.round((iosData.secondsCount + androidSeconds + webSeconds) / 3 / 60), // Average in minutes
      };
    });

    // Add any Android data that doesn't have matching iOS data
    stats.android.forEach((androidData) => {
      const exists = combinedData.some(
        (data) =>
          data.date === format(new Date(androidData.month + "-01"), "MMM yyyy")
      );
      if (!exists) {
        const webData = stats.web.find((data) => data.month === androidData.month);
        const webSeconds = webData ? webData.secondsCount : 0;
        combinedData.push({
          date: format(new Date(androidData.month + "-01"), "MMM yyyy"),
          ios: 0,
          android: Math.round(androidData.secondsCount / 60), // Convert to minutes
          web: Math.round(webSeconds / 60), // Convert to minutes
          average: Math.round((androidData.secondsCount + webSeconds) / 2 / 60), // Average in minutes
        });
      }
    });

    // Add any Web data that doesn't have matching iOS or Android data
    stats.web.forEach((webData) => {
      const exists = combinedData.some(
        (data) =>
          data.date === format(new Date(webData.month + "-01"), "MMM yyyy")
      );
      if (!exists) {
        combinedData.push({
          date: format(new Date(webData.month + "-01"), "MMM yyyy"),
          ios: 0,
          android: 0,
          web: Math.round(webData.secondsCount / 60), // Convert to minutes
          average: Math.round(webData.secondsCount / 60), // Average in minutes
        });
      }
    });

    // Sort by date
    return combinedData.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  };

  // Show full page loader only on initial load
  if (initialLoad && loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const getChartData = () => {
    if (groupBy === "daily" && appInstallsStats) {
      return prepareDailyChartData(appInstallsStats);
    } else if (groupBy === "monthly" && monthlyAppInstallsStats) {
      return prepareMonthlyChartData(monthlyAppInstallsStats);
    }
    return [];
  };

  const getEngagementChartData = () => {
    if (groupBy === "daily" && engagementTimeStats) {
      return prepareDailyEngagementChartData(engagementTimeStats);
    } else if (groupBy === "monthly" && monthlyEngagementTimeStats) {
      return prepareMonthlyEngagementChartData(monthlyEngagementTimeStats);
    }
    return [];
  };

  const getTotalStats = () => {
    if (groupBy === "daily" && appInstallsStats) {
      return {
        totalIos: appInstallsStats.totalIos,
        totalAndroid: appInstallsStats.totalAndroid,
        totalWeb: appInstallsStats.totalWeb,
      };
    } else if (groupBy === "monthly" && monthlyAppInstallsStats) {
      return {
        totalIos: monthlyAppInstallsStats.totalIos,
        totalAndroid: monthlyAppInstallsStats.totalAndroid,
        totalWeb: monthlyAppInstallsStats.totalWeb,
      };
    }
    return { totalIos: 0, totalAndroid: 0, totalWeb: 0 };
  };

  const getEngagementTotalStats = () => {
    if (groupBy === "daily" && engagementTimeStats) {
      return {
        totalSecondsIos: engagementTimeStats.totalSecondsCountIos,
        totalSecondsAndroid: engagementTimeStats.totalSecondsCountAndroid,
        totalSecondsWeb: engagementTimeStats.totalSecondsCountWeb,
      };
    } else if (groupBy === "monthly" && monthlyEngagementTimeStats) {
      return {
        totalSecondsIos: monthlyEngagementTimeStats.totalSecondsCountIos,
        totalSecondsAndroid:
          monthlyEngagementTimeStats.totalSecondsCountAndroid,
        totalSecondsWeb: monthlyEngagementTimeStats.totalSecondsCountWeb,
      };
    }
    return { totalSecondsIos: 0, totalSecondsAndroid: 0, totalSecondsWeb: 0 };
  };

  const totalStats = getTotalStats();
  const engagementTotalStats = getEngagementTotalStats();

  const lifetimeChartData = lifetimeStats
    ? prepareLifetimeChartData(lifetimeStats)
    : [];

  const chartData = getChartData();
  const engagementChartData = getEngagementChartData();

  return (
    <div>
      {/* Lifetime Stats Section */}
      <div className="mb-12">
        {lifetimeStatsLoading ? (
          <div className="flex justify-center my-8">
            <Loader />
          </div>
        ) : lifetimeStatsError ? (
          <div className="error mb-4">{lifetimeStatsError}</div>
        ) : lifetimeStats ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap">
              {/* Distribution Pie Chart */}
              <div className="w-full md:w-1/2 max-w-md">
                <h4 className="text-lg font-medium mb-4">
                  Lifetime App Downloads by Platform
                </h4>
                <div className="h-[300px] relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={lifetimeChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={110}
                        isAnimationActive={false}
                        labelLine={true}
                        label={({ name, value }) =>
                          `${name}: ${value.toLocaleString()}`
                        }
                      >
                        {lifetimeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div
                      className="text-xl font-bold"
                      style={{ color: BAR_COLORS.total }}
                    >
                      {(
                        lifetimeStats.totalIos + lifetimeStats.totalAndroid + lifetimeStats.totalWeb
                      ).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">Total Downloads</div>
                  </div>
                </div>
                {renderCustomLegend(lifetimeChartData)}
              </div>

              {/* Lifetime Engagement Stats */}
              <div className="w-full md:w-1/2 max-w-md mt-8 md:mt-0">
                <h4 className="text-lg font-medium mb-4">
                  Lifetime Average Engagement Time
                </h4>

                {lifetimeEngagementStatsError ? (
                  <div className="error mb-4">
                    {lifetimeEngagementStatsError}
                  </div>
                ) : lifetimeEngagementStats ? (
                  <div className="flex flex-col gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className="h-3 w-3 rounded-full mr-2"
                          style={{ backgroundColor: PLATFORM_COLORS.iOS }}
                        ></div>
                        <span className="text-sm font-medium">iOS</span>
                      </div>
                      <div className="text-lg font-semibold">
                        {formatEngagementTime(
                          lifetimeEngagementStats.secondsCountIos
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className="h-3 w-3 rounded-full mr-2"
                          style={{ backgroundColor: PLATFORM_COLORS.Android }}
                        ></div>
                        <span className="text-sm font-medium">Android</span>
                      </div>
                      <div className="text-lg font-semibold">
                        {formatEngagementTime(
                          lifetimeEngagementStats.secondsCountAndroid
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className="h-3 w-3 rounded-full mr-2"
                          style={{ backgroundColor: PLATFORM_COLORS.Web }}
                        ></div>
                        <span className="text-sm font-medium">Web</span>
                      </div>
                      <div className="text-lg font-semibold">
                        {formatEngagementTime(
                          lifetimeEngagementStats.secondsCountWeb
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 flex justify-between items-center mt-2">
                      <div className="flex items-center">
                        <div
                          className="h-3 w-3 rounded-full mr-2"
                          style={{ backgroundColor: BAR_COLORS.total }}
                        ></div>
                        <span className="text-sm font-medium">Average</span>
                      </div>
                      <div className="text-lg font-semibold">
                        {formatEngagementTime(
                          (lifetimeEngagementStats.secondsCountIos +
                            lifetimeEngagementStats.secondsCountAndroid +
                            lifetimeEngagementStats.secondsCountWeb) /
                            3
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center my-8">
                    <Loader />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <h3 className="text-lg font-medium">Time-based Analytics</h3>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group By
            </label>
            <select
              value={groupBy}
              onChange={handleGroupByChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        {groupBy === "daily" && (
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant={activeDailyRange === "7days" ? "primary" : "outline"}
                onClick={() => handleDailyRangeClick("7days")}
              >
                Last 7 Days
              </Button>
              <Button
                variant={activeDailyRange === "30days" ? "primary" : "outline"}
                onClick={() => handleDailyRangeClick("30days")}
              >
                Last 30 Days
              </Button>
              <Button
                variant={activeDailyRange === "2months" ? "primary" : "outline"}
                onClick={() => handleDailyRangeClick("2months")}
              >
                Last Two Months
              </Button>
            </div>
          </div>
        )}

        {groupBy === "monthly" && (
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Month
              </label>
              <select
                value={fromMonth}
                onChange={handleFromMonthChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Select month</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Month
              </label>
              <select
                value={toMonth}
                onChange={handleToMonthChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Select month</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                variant={
                  activeMonthlyRange === "last3Months" ? "primary" : "outline"
                }
                onClick={() => handleMonthlyRangeClick("last3Months")}
              >
                Last 3 Months
              </Button>
              <Button
                variant={
                  activeMonthlyRange === "last6Months" ? "primary" : "outline"
                }
                onClick={() => handleMonthlyRangeClick("last6Months")}
              >
                Last 6 Months
              </Button>
              <Button
                variant={
                  activeMonthlyRange === "last12Months" ? "primary" : "outline"
                }
                onClick={() => handleMonthlyRangeClick("last12Months")}
              >
                Last 12 Months
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Show inline loader during subsequent data fetches */}
      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      {chartData.length > 0 ? (
        <>
          {/* Totals */}
          <div className="flex gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
              <div className="text-gray-500 text-sm">iOS Downloads</div>
              <div className="text-2xl font-semibold mt-1">
                {totalStats.totalIos.toLocaleString()}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
              <div className="text-gray-500 text-sm">Android Downloads</div>
              <div className="text-2xl font-semibold mt-1">
                {totalStats.totalAndroid.toLocaleString()}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
              <div className="text-gray-500 text-sm">Web Downloads</div>
              <div className="text-2xl font-semibold mt-1">
                {totalStats.totalWeb.toLocaleString()}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
              <div className="text-gray-500 text-sm">Total Downloads</div>
              <div className="text-2xl font-semibold mt-1">
                {(
                  totalStats.totalIos + totalStats.totalAndroid + totalStats.totalWeb
                ).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-medium mb-4 text-center">
              App Downloads Over Time
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={90}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend wrapperStyle={{ paddingTop: 20 }} />
                  <Bar
                    name="iOS Downloads"
                    dataKey="ios"
                    fill={BAR_COLORS.ios}
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Android Downloads"
                    dataKey="android"
                    fill={BAR_COLORS.android}
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Web Downloads"
                    dataKey="web"
                    fill={BAR_COLORS.web}
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    name="Total Downloads"
                    dataKey="total"
                    fill={BAR_COLORS.total}
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Engagement Time Stats Section */}
          {engagementTimeLoading ? (
            <div className="flex justify-center my-8">
              <Loader />
            </div>
          ) : engagementTimeError ? (
            <div className="error mb-4">{engagementTimeError}</div>
          ) : engagementChartData.length > 0 ? (
            <>
              {/* Engagement Time Totals */}
              <div className="flex gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
                  <div className="text-gray-500 text-sm">
                    iOS Avg Engagement
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatEngagementTime(engagementTotalStats.totalSecondsIos)}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
                  <div className="text-gray-500 text-sm">
                    Android Avg Engagement
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatEngagementTime(
                      engagementTotalStats.totalSecondsAndroid
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
                  <div className="text-gray-500 text-sm">
                    Web Avg Engagement
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatEngagementTime(
                      engagementTotalStats.totalSecondsWeb
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
                  <div className="text-gray-500 text-sm">
                    Overall Avg Engagement
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatEngagementTime(
                      (engagementTotalStats.totalSecondsIos +
                        engagementTotalStats.totalSecondsAndroid +
                        engagementTotalStats.totalSecondsWeb) /
                        3
                    )}
                  </div>
                </div>
              </div>

              {/* Engagement Time Chart */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-medium mb-4 text-center">
                  Average Engagement Time Over Time (in minutes)
                </h3>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={engagementChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={90}
                        interval={0}
                      />
                      <YAxis unit="m" />
                      <Tooltip formatter={(value) => `${value} min`} />
                      <Legend wrapperStyle={{ paddingTop: 20 }} />
                      <Bar
                        name="iOS Engagement (min)"
                        dataKey="ios"
                        fill={BAR_COLORS.ios}
                        barSize={20}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name="Android Engagement (min)"
                        dataKey="android"
                        fill={BAR_COLORS.android}
                        barSize={20}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name="Web Engagement (min)"
                        dataKey="web"
                        fill={BAR_COLORS.web}
                        barSize={20}
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name="Average Engagement (min)"
                        dataKey="average"
                        fill={BAR_COLORS.total}
                        barSize={20}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 my-8">
              No engagement time data available for the selected date range
            </div>
          )}
        </>
      ) : !loading && !error ? (
        <div className="text-center text-gray-500 my-8">
          No data available for the selected date range
        </div>
      ) : null}
    </div>
  );
};

export default AdminAppPerformance;
